import moment from 'moment';

import { DateFormat } from '../../utils/date';
import UserContract from '../../Model/UserContract';
import ChallengeConfig from '../Model/ChallengeConfig';

/**
 * `userContract.startDate` is a date-only string, parsed as UTC so the cutoff
 * lands on the same day the server computes regardless of the coach's timezone.
 */
const parseContractDate = (startDate) => moment.utc(startDate, DateFormat.DEFAULT_DATE_FORMAT, true);

const DISPLAY_DATE_FORMAT = 'MMMM D, YYYY';

/**
 * A challenge's start date as the propagation notice shows it.
 *
 * @param {string} startDate - The base contract's start date, as `YYYY-MM-DD`.
 * @returns {string}
 */
const formatChallengeStartDate = (startDate) => parseContractDate(startDate).format(DISPLAY_DATE_FORMAT);

/**
 * Whether edits to what a challenge hands out on join (its program, its meal
 * plan) still reach the clients who already signed up.
 *
 * Mirrors the server-side cutoff: the start day itself counts as started, and a
 * challenge whose start date can't be resolved never propagates.
 *
 * @param {?string} startDate - The base contract's start date, as `YYYY-MM-DD`.
 * @returns {boolean}
 */
const isChallengeOpenForEdits = (startDate) => {
  if (!startDate) {
    return false;
  }
  const parsed = parseContractDate(startDate);
  return parsed.isValid() && parsed.startOf('day').isAfter(moment.utc().startOf('day'));
};

/**
 * Describes what saving a program or meal plan template will do to a challenge's
 * signed-up clients, or null when there is nothing to warn about — the template
 * is not part of a challenge, or every challenge using it has already started.
 *
 * A program counts whether it is a challenge's single program or its home or gym one.
 *
 * The challenge's name and start date both come from its BASE contract, the one
 * row per challenge that every participant's contract is copied from. Its `name`
 * is the challenge's own; `productName` names the product the challenge sells.
 *
 * @param {{programId?: string, mealPlanId?: string}} template - The template about to be saved.
 * @returns {Promise<?{challengeName: string, startDate: string}>} The first open challenge found.
 */
const getChallengePropagation = async ({ programId, mealPlanId }) => {
  let challengeConfigs;
  if (programId) {
    challengeConfigs = await ChallengeConfig.getChallengeConfigsByProgramId(programId);
  } else if (mealPlanId) {
    challengeConfigs = await ChallengeConfig.getChallengeConfigsByMealPlanId(mealPlanId);
  } else {
    throw new Error('getChallengePropagation needs a programId or a mealPlanId');
  }

  // A template rarely sits on more than a couple of challenges, so their base
  // contracts are read together rather than one at a time.
  const baseContracts = await Promise.all(
    challengeConfigs.map(({ id }) => UserContract.getBaseContractByChallengeId(id)),
  );
  const openContract = baseContracts.find((baseContract) => (
    !!baseContract && isChallengeOpenForEdits(baseContract.startDate)
  ));

  if (openContract) {
    return {
      challengeName: openContract.name || '',
      startDate: openContract.startDate,
    };
  }

  return null;
};

export {
  isChallengeOpenForEdits,
  formatChallengeStartDate,
  getChallengePropagation,
};
